import { StyledModalDialog } from "@src/common/styled-components";
import { LAYOUT_CONSTANTS } from "@src/constants";
import { useApplicationContext } from "@src/context/ApplicationContext";
import ClientLogsService from "@src/services/client-logs.service";
import type { AgeModalScenario } from "@src/utils/age-modal-guard";
import { Button } from "@uhg-abyss/web/ui/Button";
import type { ReactNode } from "react";
import { useEffect } from "react";

export type UpdateAgeModalVariant = AgeModalScenario;

const MODAL_CONFIG: Record<
  UpdateAgeModalVariant,
  { buttonLabel: string; header: string; obcLabel: string }
> = {
  age_range_mismatch: {
    buttonLabel: "Update date of birth",
    header: "Age range mismatch",
    obcLabel: "Update date of birth",
  },
  age_range_reset_confirmation: {
    buttonLabel: "Continue",
    header: "Your information has been saved",
    obcLabel: "Age Range Reset Confirmation Continue Button",
  },
  in_person_visit: {
    buttonLabel: "Proceed",
    header: "In-person visit required",
    obcLabel: "Update Age Modal In-Person Visit Proceed Button",
  },
};

interface UpdateAgeModalProps {
  isOpen: boolean;
  onClose?: () => void;
  onConfirm?: () => void;
  text: ReactNode;
  header?: string;
  variant: UpdateAgeModalVariant;
  hideUpdateAgeButton?: boolean;
}

export const UpdateAgeModal = ({
  isOpen,
  onClose,
  onConfirm,
  text,
  header,
  variant,
  hideUpdateAgeButton = false,
}: UpdateAgeModalProps) => {
  const { data } = useApplicationContext();
  const config = MODAL_CONFIG[variant];

  useEffect(() => {
    if (isOpen) {
      ClientLogsService.logWebAction(
        data.userUuid || "unknown",
        "Under18Modal",
        "User identified as under 18, showing modal",
      );
    }
  }, [isOpen, data]);

  const handlePrimaryClick = () => {
    if (variant === "in_person_visit") {
      onConfirm?.();
    }
    onClose?.();
  };

  const handleUpdateAgeClick = () => {
    if (variant === "age_range_mismatch") {
      onConfirm?.();
      return;
    }
    onClose?.();
  };

  const modalFooter = (
    <div
      style={{
        display: "flex",
        justifyContent: variant === "age_range_reset_confirmation" ? "flex-end" : "flex-start",
        width: "100%",
        paddingTop: "16px",
      }}
    >
      <Button
        data-testid="test-id-update-age-modal-close-button"
        id="obc-update-age-modal-close-btn"
        data-obc-label={config.obcLabel}
        onClick={handlePrimaryClick}
      >
        {config.buttonLabel}
      </Button>

      {!hideUpdateAgeButton && variant !== "age_range_reset_confirmation" && (
        <Button
          data-testid="test-id-update-age-modal-update-age-button"
          id="obc-update-age-modal-update-age-btn"
          data-obc-label="Update Age Modal Update Age Button"
          variant="outline"
          onClick={handleUpdateAgeClick}
          style={{ marginLeft: "16px" }}
        >
          Update age range
        </Button>
      )}
    </div>
  );

  return (
    <StyledModalDialog
      data-testid="test-id-update-age-modal"
      header={header ?? config.header}
      isOpen={isOpen}
      size={LAYOUT_CONSTANTS.modalDialogWidth}
      footer={modalFooter}
      hideClose
    >
      {text}
    </StyledModalDialog>
  );
};
